import { defineStore } from 'pinia'
import { ref } from 'vue'
import childDocumentService from '@/services/http/api/childDocument'
import { FILE_SIZES } from '@/constants/files'
import { FetchedDrawingSchema } from '@/schemas/childDocument'

import { AxiosError } from 'axios'
import type { TODO } from '@/types'

const emptyDreamDrawing = (): TODO => ({
  likeDescription: null,
  drawing: null, // Blob
  drawingOriginalSize: null,
  drawingMediumSize: null,
  drawingThumbnailSize: null
})

const emptyFetchedDrawingFiles = (): TODO =>
  FILE_SIZES.reduce(
    (previous, current) => ({
      ...previous,
      [current]: {
        file: null,
        loading: false
      }
    }),
    {}
  )

export const useDreamDrawingStore = defineStore('dreamDrawing', () => {
  const fetchDrawingFiles = ref(false)
  const fetchedDrawingFiles = ref(emptyFetchedDrawingFiles())
  const dreamDrawing = ref(emptyDreamDrawing())

  const clearDreamDrawing = () => {
    dreamDrawing.value = emptyDreamDrawing()
    fetchedDrawingFiles.value = emptyFetchedDrawingFiles()
  }

  const setDreamDrawing = (fields: TODO) => {
    if (!fields) return
    Object.entries(dreamDrawing.value).forEach(([key]) => {
      if (!fields[key]) return
      dreamDrawing.value[key] = fields[key]
    })
    fetchDrawingFiles.value = true
  }

  const setFetchedDrawingFiles = (payload: TODO) => {
    FetchedDrawingSchema.parse(payload)
    fetchedDrawingFiles.value = {
      ...fetchedDrawingFiles.value,
      [payload.size]: {
        file: payload.file,
        loading: payload.loading
      }
    }
  }

  const setDrawing = (file: Blob) => {
    dreamDrawing.value.drawing = file
  }

  const fetchDrawingFileBySize = async (payload: TODO) => {
    try {
      if (payload?.size) {
        setFetchedDrawingFiles({
          file: null,
          size: payload.size,
          loading: true
        })
        const drawing = await childDocumentService.fetchDrawingFileBySize(
          payload,
          payload?.getAssignedFirst
        )
        setFetchedDrawingFiles({
          file: drawing?.data ?? null,
          size: payload.size,
          loading: false
        })
      }
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error?.response?.status === 403 &&
        payload?.getAssignedFlag &&
        !payload?.getAssignedFirst
      ) {
        await fetchDrawingFileBySize({ ...payload, getAssignedFirst: true })
      } else throw error
    } finally {
      fetchDrawingFiles.value = false
    }
  }
  return {
    dreamDrawing,
    fetchedDrawingFiles,
    fetchDrawingFiles,
    clearDreamDrawing,
    setDreamDrawing,
    setFetchedDrawingFiles,
    setDrawing,
    fetchDrawingFileBySize
  }
})
